<template>
  <div>
    <Header />

    <div class="title container-fluid text-center">
      <h1><strong>Terms and Conditions</strong></h1>
    </div>

    <div class="content container">
      <p>
        Welcome to
        <strong>VIVA</strong>,
        a platform provided by Aim Solutions, Inc. (doing business as
        <strong>InitialView</strong>)
        (”<strong>InitialView</strong>”,
        “<strong>we</strong>”,
        or
        “<strong>us</strong>”).
        These Terms and Conditions
        (”<strong>Terms</strong>”)
        govern your access to and use of the VIVA website, applications, and
        services (collectively, the
        “<strong>Service</strong>”).
        By accessing or using VIVA, you agree to be bound by these Terms. If
        you do not agree, you must not use the Service.
      </p>
      <p>
        We may update these Terms from time to time by posting a revised version
        on this page. The revised Terms will take effect immediately upon posting,
        unless otherwise stated.
      </p>

      <h3><strong>1. Acceptance of Terms</strong></h3>
      <p>
        By using VIVA, you acknowledge that you have read, understood, and
        agree to these Terms, as well as our Privacy Policy (which explains how
        we handle your personal data). If you are using the Service on behalf of
        an organization (such as a school), you represent that you are
        authorized to accept these Terms on that organization’s behalf.
      </p>

      <h3><strong>2. Description of the Service</strong></h3>
      <p>
        VIVA is an educational platform that helps verify students’
        understanding of their own project through an oral Q&A process.
      </p>
      <p>
        Our mission is to help students and other individuals demonstrate their
        understanding of academic material in a way reminiscent of a viva voce
        exam. The Service may continue to evolve, and we may add, remove, or
        modify features over time.
      </p>

      <h3><strong>3. Eligibility and Minor Users</strong></h3>
      <p>
        <strong>Minimum Age:</strong>
        You must be at least 13 years old to use VIVA. The Service is not
        intended for children under 13, and we do not knowingly allow children
        under 13 to use the platform in compliance with COPPA and similar
        regulations. If you are under 13,
        <strong>do not use this Service</strong>.
      </p>
      <p>
        <strong>Users 13-17 (Minors):</strong>
        If you are under 18 years of age (or under the age of majority in your
        jurisdiction, if different) but at least 13 years old, you may only use
        VIVA
        <strong>
          with the consent and under the supervision of your parent or legal guardian
        </strong>.
        By using the Service, you represent and warrant that:
      </p>
      <ul>
        <li>
          <p>
            You have your parent or legal guardian’s permission to use VIVA,
            and your parent/guardian has read and agreed to these Terms (we may
            require verifiable consent in some cases).
          </p>
        </li>
        <li>
          <p>
            If you are using VIVA as part of a school program, your school or
            teacher has any required permission from your parent/guardian to allow
            your participation.
          </p>
        </li>
      </ul>
      <p>
        <strong>Parental/Guardian Responsibility:</strong>
        If you are a parent or legal guardian permitting a minor to use VIVA,
        you agree to these Terms on the minor’s behalf. You are responsible for
        supervising the minor’s use of the Service and
        <strong>
          you agree to indemnify and hold us harmless for the minor’s violation of
          these Terms
        </strong>.
        Please discuss these Terms and the importance of safety and honesty with
        your child before they use VIVA.
      </p>

      <h3><strong>4. Accounts and Registration</strong></h3>
      <p>
        To use VIVA, you (or your school) may need to create an account or
        register on the platform:
      </p>
      <ul>
        <li>
          <p>
            <strong>Account Information:</strong>
            You agree to provide accurate, current, and complete information
            during registration. This may include your name, school, email, and
            other contact information.
          </p>
        </li>
        <li>
          <p>
            <strong>Account Security:</strong>
            You are responsible for maintaining the confidentiality of your
            account login credentials. Do not share your username or password with
            others. You are responsible for all activities that occur under your
            account. If you believe your account has been compromised or used
            without authorization, notify us immediately at
            <a href="mailto:contact@initialview.com">contact@initialview.com</a>.
          </p>
        </li>
        <li>
          <p>
            <strong>Authorized Use Only:</strong>
            Do not create an account using a false identity or on behalf of
            someone other than yourself (except as allowed for a parent/guardian
            or school official managing student accounts). Each student should
            have their own account (unless a school arrangement dictates
            otherwise).
          </p>
        </li>
      </ul>
      <p>
        We reserve the right to suspend or terminate any account that we
        reasonably suspect is being used in violation of these Terms or any
        applicable law.
      </p>

      <h3><strong>5. Fees and Payment</strong></h3>
      <p>
        Use of the VIVA platform is a paid service in most cases:
      </p>
      <ul>
        <li>
          <p>
            <strong>Student-Paid Model:</strong>
            If you are a student using VIVA for college applications or
            personal use, you (or your parent/guardian) may be required to pay a
            fee to record your Q&A session and share it with colleges. All fees
            will be disclosed at the time of purchase or registration. By
            submitting payment information, you authorize us (or our payment
            processor) to charge the applicable fees to your payment method.
          </p>
        </li>
        <li>
          <p>
            <strong>School-Paid Model:</strong>
            If your high school or educational institution provides access to VIVA
            (for example, to enforce academic honesty), the school may pay
            for the Service on your behalf. In such cases, you might be given an
            access code or login by your school. You are still individually bound
            by these Terms when using the Service, even if a school arranged your
            access.
          </p>
        </li>
        <li>
          <p>
            <strong>No Refunds:</strong>
            Fees are generally non-refundable once the service is utilized. For
            example, if you complete a recording or send it to colleges, we will
            not issue a refund even if you decide not to use the video or are
            unhappy with the outcome. However, if you experience a technical issue
            or believe a refund is warranted, you can contact us and we will
            review requests on a case-by-case basis.
          </p>
        </li>
        <li>
          <p>
            <strong>Changes in Fees:</strong>
            We reserve the right to change our pricing or introduce new charges
            for additional features in the future. If you do not agree to new
            fees, you may discontinue use of the paid features before the new
            charges apply.
          </p>
        </li>
      </ul>
      <p>
        We reserve the right to explore additional revenue models (such as
        advertising or data licensing) in the future – if we do so, we will
        update our Terms and/or Privacy Policy and notify users as required.
      </p>

      <h3><strong>6. User Content and Ownership Rights</strong></h3>

      <h4><strong>6.1 Types of User Content</strong></h4>
      <p>
        By using VIVA, you will be providing or creating various content
        (”<strong>User Content</strong>”),
        including but not limited to:
      </p>
      <ul>
        <li>
          <p>
            <strong>Uploaded Work:</strong>
            The project that you upload for analysis.
          </p>
        </li>
        <li>
          <p>
            <strong>AI-Generated Questions:</strong>
            The questions or prompts that our AI systems generate based on your
            uploaded work (these may be considered part of our Service content,
            but they are derived from your material).
          </p>
        </li>
        <li>
          <p>
            <strong>Recorded Responses (Videos):</strong>
            Your audio/video recordings of answers to the questions, including
            your voice, image (likeness), and anything you say or display in the
            video.
          </p>
        </li>
        <li>
          <p>
            <strong>Other Submissions:</strong>
            Any feedback, text responses, comments, profiles, or other data you
            input into the platform.
          </p>
        </li>
      </ul>

      <h4><strong>6.2 Ownership of Recordings and Content</strong></h4>
      <p>
        You retain ownership of any intellectual property rights you hold in your
        original content
        <strong>before</strong>
        submitting it to VIVA (for example, you likely own the copyright to
        your project, and using our Service does not transfer that
        original ownership to us).
        <strong>
          However, any recordings or new content created through the VIVA
          Service (such as the video of your answers, associated Q&A data, etc.)
          will be owned by InitialView (Aim Solutions, Inc.)
        </strong>.
        In other words, the interview videos and any related materials produced
        via the
        <strong>
          Service are the property of InitialView or its affiliated entities
        </strong>.
      </p>
      <p>
        To the extent that ownership of any such Service-related content does
        not automatically vest in us, you
        <strong>assign</strong>
        to us all rights, title, and interest in and to the recordings and related
        content created through the Service. This assignment is worldwide,
        perpetual, irrevocable, and royalty-free. It allows us (and our affiliates
        and contractors) to fully exploit the recordings and derived content for
        any legitimate purpose consistent with our Privacy Policy and these Terms.
      </p>

      <h4><strong>6.3 License Grant from You to Us</strong></h4>
      <p>
        For any User Content that is not automatically owned by us (for example,
        your uploaded project, or any content you submit that is not
        created by the Service itself), you hereby grant InitialView and its
        affiliates a broad license to use that content. Specifically,
        <strong>
          you grant us a non-exclusive, worldwide, perpetual, irrevocable,
          fully-paid and royalty-free license, with the right to sublicense, to
        </strong>:
      </p>
      <ul>
        <li>
          <p>
            <strong>Use, reproduce, and store</strong>
            your content on our systems and servers for the purpose of providing
            the Service (e.g., analyzing your project and storing your video).
          </p>
        </li>
        <li>
          <p>
            <strong>Distribute and share</strong>
            your content as needed to fulfill your requests (for example,
            transmitting your video and related materials to the colleges or
            educators you designate, or to third-party service providers like AI
            processing services that help operate VIVA).
          </p>
        </li>
        <li>
          <p>
            <strong>Modify, translate, or create derivative works</strong>
            from your content as necessary for technical processing or improving
            the Service (e.g., generating transcripts of your video, extracting
            text from your uploads, or using snippets for product development).
          </p>
        </li>
        <li>
          <p>
            <strong>Display and perform</strong>
            your content (in the case of videos, playing them for those you
            authorize to view, such as admissions officers or teachers, and
            potentially for internal demonstrations or marketing if you have given
            consent).
          </p>
        </li>
        <li>
          <p>
            <strong>Use your content for research and development:</strong>
            This includes using your content to train, tune, or improve artificial
            intelligence (AI) and machine learning models, to improve our
            question-generation algorithms, or to develop new features. By using
            VIVA, you acknowledge and agree that we may use the content of
            your projects, questions, recordings, and feedback to develop and
            enhance our AI systems and services.
          </p>
        </li>
      </ul>
      <p>
        This license to your content is granted to us
        <strong>without any compensation to you</strong>.
        You agree that we may use your content and likeness as described above,
        and you waive any right of privacy, publicity, or any other rights of a
        similar nature in the context of these uses. If applicable law does not
        allow a complete waiver of such rights, you agree that our use of the
        content for the purposes stated is sufficient consideration.
      </p>

      <h4><strong>6.4 Your Warranties and Responsibilities for Content</strong></h4>
      <p>
        When you upload or record content on VIVA, you are responsible for
        that content. You represent and warrant that:
      </p>
      <ul>
        <li>
          <p>
            <strong>Your own work:</strong>
            The project you upload is your own original work (or
            you have all necessary rights and permissions from the rightful owner
            to use it for this purpose). The same goes for any other materials you
            submit.
          </p>
        </li>
        <li>
          <p>
            <strong>No infringement:</strong>
            Your content does not infringe or misappropriate any copyright,
            patent, trademark, trade secret, moral rights, privacy rights,
            publicity rights, or other rights of any person or entity. For
            example, you’re not uploading someone else’s project without permission.
          </p>
        </li>
        <li>
          <p>
            <strong>No unlawful or harmful content:</strong>
            Your content is not defamatory, obscene, pornographic, hateful, or
            otherwise unlawful, and it does not contain any viruses, malware, or
            harmful code.
          </p>
        </li>
        <li>
          <p>
            <strong>Consent for others’ data:</strong>
            If your content contains personal information of others (e.g., if you
            mention a person by name or include someone else’s image), you have
            obtained all necessary consents to include that information and to
            allow us to use and share it as described in these Terms.
          </p>
        </li>
      </ul>
      <p>
        You are solely responsible for the content you provide and for the
        consequences of any false, infringing, or unlawful content.
        <strong>
          You agree to indemnify us for any costs or damages that arise from your
          breach of these warranties
        </strong>
        (see Section 12 on Indemnification below for more details).
      </p>

      <h4><strong>6.5 Our Use of Content and No Confidentiality</strong></h4>
      <p>
        Except as otherwise provided in our Privacy Policy, any content you
        submit through the Service may be used by us for the purposes described
        in these Terms. While we do value your privacy (and will handle personal
        data in accordance with our Privacy Policy), we do
        <strong>not</strong>
        treat the general content you provide (like the text of a project
        or the recorded answers) as strictly confidential or owned by you once
        submitted. For example, we might analyze aggregated data from many
        students’ videos to publish a report on learning outcomes, or use
        snippets (without full names) as examples in training materials or
        research publications. If we do so, we will comply with applicable
        privacy laws and take steps to avoid disclosing personally identifying
        details without consent.
      </p>
      <p>
        If you have an agreement with a school or organization that claims
        ownership or confidentiality in your work, it is your responsibility to
        ensure using VIVA does not violate that agreement. We cannot know
        or honor third-party agreements you have – we operate under these Terms
        with you.
      </p>

      <h3><strong>7. License to Use Our Service</strong></h3>
      <p>
        Subject to your compliance with these Terms, we grant you a limited,
        non-exclusive, non-transferable, revocable license to access and use the
        VIVA platform and content
        <strong>for its intended educational and evaluative purposes</strong>.
        This allows you to upload your work, generate AI questions, record your
        answers, and share/submit them as part of schoolwork or applications, and
        in the case of use by administrators, teachers, or evaluators, to record
        feedback to students.
        <strong>
          All rights not expressly granted to you are reserved by us
        </strong>.
      </p>
      <p>
        You agree that you will
        <strong>
          only use VIVA for personal, educational, and non-commercial purposes
        </strong>
        (for students) or for bona fide educational or other evaluation (for
        teachers/administrators/evaluators). You may not resell access to VIVA
        or use it for the benefit of any third party not authorized by us.
      </p>

      <h4><strong>7.1 Prohibited Uses and Conduct</strong></h4>
      <p>
        For clarity, you
        <strong>agree NOT to</strong>
        engage in any of the following activities:
      </p>
      <ul>
        <li>
          <p>
            <strong>Unlawful Use:</strong>
            Do not use the Service to violate any law or regulation. This includes
            cheating or academic fraud (e.g., do not use VIVA to pretend you
            created a project that you didn’t, or to misrepresent your abilities to
            an institution).
          </p>
        </li>
        <li>
          <p>
            <strong>Impersonation or Misrepresentation:</strong>
            Do not impersonate any person or entity, or misrepresent your identity
            or affiliation. In particular,
            <strong>
              you must not have another person answer questions in your place or
              otherwise falsify the authenticity of the video
            </strong>.
            The whole point of VIVA is to demonstrate your own
            knowledge – having someone else assist or stand-in is strictly
            forbidden.
          </p>
        </li>
        <li>
          <p>
            <strong>Infringing Content:</strong>
            Do not upload or share content that infringes on the intellectual
            property or privacy rights of others. For example, do not upload a
            project copied from someone else, or include copyrighted images without
            permission.
          </p>
        </li>
        <li>
          <p>
            <strong>Harassment or Harm:</strong>
            Do not use the Service to harass, bully, or harm others. This includes
            refraining from using hateful language or personal attacks in your
            recordings or interactions.
          </p>
        </li>
        <li>
          <p>
            <strong>Tampering and Unauthorized Access:</strong>
            Do not attempt to interfere with the operation of the Service or
            circumvent any restrictions. This means
            <strong>
              no hacking, scanning, or exploiting the platform
            </strong>,
            and do not attempt to manipulate the recording process (such as by
            splicing in pre-recorded footage, using voice-altering software to
            deceive, etc.).
          </p>
        </li>
        <li>
          <p>
            <strong>Reverse Engineering:</strong>
            You must not reverse engineer, decompile, or disassemble any aspect of
            the Service, nor attempt to extract source code or underlying
            algorithms or prompts. Similarly, do not modify or create derivative
            works based on the Service or our content, except as allowed by us.
          </p>
        </li>
        <li>
          <p>
            <strong>Automated Access:</strong>
            Do not use bots, scrapers, or other automated means to access or
            collect information from VIVA without our express permission.
          </p>
        </li>
        <li>
          <p>
            <strong>Commercial Use:</strong>
            Do not use the Service for commercial advertising, promotions, or any
            money-making ventures outside the scope of these Terms. (For instance,
            you cannot sell your VIVA video or license it to others –
            remember, the video is our property once created).
          </p>
        </li>
        <li>
          <p>
            <strong>Interference:</strong>
            Do not upload viruses, malware, or any other code that could harm the
            Service or other users. Also, avoid any actions that impose an
            unreasonable load on our infrastructure or that could disrupt the
            normal flow of the Q&A sessions.
          </p>
        </li>
      </ul>
      <p>
        Violation of any of the above can result in immediate termination of
        your account and use of the Service, as well as potential legal action
        if warranted. We reserve the right to monitor the platform for misuse
        and to investigate any alleged violations.
      </p>

      <h3><strong>8. Third-Party Services and AI Tools</strong></h3>

      <h4><strong>8.1 Use of AI (ChatGPT and LLMs)</strong></h4>
      <p>
        VIVA leverages advanced
        <strong>third-party AI services</strong>
        (such as OpenAI’s GPT models and potentially other large language models)
        to analyze text and generate questions. By using the Service, you
        acknowledge and agree that:
      </p>
      <ul>
        <li>
          <p>
            <strong>AI Processing:</strong>
            The content you upload (e.g., your project) will be sent
            to and processed by third-party AI providers (like OpenAI) in order to
            generate questions and otherwise facilitate the Service. We will take
            reasonable measures to transmit and store this data securely, but you
            understand that third-party providers will receive the data for
            processing.
          </p>
        </li>
        <li>
          <p>
            <strong>Consent to Processing:</strong>
            You consent to this use of
            <strong>your content by third-party AI services</strong>. 
            By using VIVA, you give us the permission needed to utilize such
            AI tools on your behalf.
          </p>
        </li>
        <li>
          <p>
            <strong>AI Output Limitations:</strong>
            AI-generated questions or feedback may not always be perfect or
            accurate. While we hope the AI provides insightful and relevant
            questions,
            <strong>
              we do not guarantee the quality, correctness, or appropriateness of
              any content generated by the AI
            </strong>.
            You acknowledge that the AI might occasionally produce irrelevant,
            awkward, or even incorrect questions. Please use your own judgment and
            preparation in responding.
          </p>
        </li>
        <li>
          <p>
            <strong>No Liability for AI Content:</strong>
            We are
            <strong>
              not liable for any issues or damages arising from the AI-generated
              content
            </strong>
            on the platform. This includes any claim that the AI’s question was
            misleading, too difficult, or caused any outcome (for example, if you
            feel a question hurt your performance). The AI is a tool to facilitate
            your explanation of your work, but final responsibility for how you
            respond and how that is perceived lies with you and the reviewer.
          </p>
        </li>
      </ul>

      <h4><strong>8.2 Other Third-Party Services</strong></h4>
      <p>
        Beyond AI providers, we may integrate other third-party services or
        platforms to help run VIVA (for example, cloud storage providers,
        video streaming or recording libraries, payment processors, etc.). When
        you use VIVA, you might also be using the services of those third
        parties (for instance, Stripe for payments or a video service for
        recording). You agree to comply with any relevant terms and conditions
        of those third-party services, and acknowledge that their privacy
        practices and terms are independent of ours. We aren’t responsible for
        the services provided by third parties, except as required by law.
      </p>
      <p>
        If you have questions about which third-party services we use for
        specific functions, please refer to our Privacy Policy or contact us for
        more information.
      </p>

      <h3><strong>9. Privacy and Data Protection</strong></h3>
      <p>
        Your privacy is important to us. Our
        <strong>Privacy Policy</strong>
        describes in detail how we collect, use, store, and disclose your personal
        information when you use VIVA. By using the Service, you agree to the
        collection and use of information in accordance with our Privacy Policy.
      </p>
      <p>
        Key points regarding privacy and data in the context of these Terms:
      </p>
      <ul>
        <li>
          <p>
            <strong>Educational Records:</strong>
            If your use of VIVA is part of a school program, the video and
            results may be considered an educational record. We will handle
            student data in compliance with applicable student privacy laws (such
            as FERPA in the U.S.) when working with schools. Schools may have
            separate agreements with us governing student data handling.
          </p>
        </li>
        <li>
          <p>
            <strong>Data Retention:</strong>
            As noted, we may retain the videos and related data for an indefinite
            period, unless deletion is required by law or we choose to delete it.
            This retention helps us allow colleges or schools to view the content
            across an application cycle and also enables us to use older data to
            improve our services over time.
          </p>
        </li>
        <li>
          <p>
            <strong>Data Sharing:</strong>
            We will share your recorded video and related info
            <strong>
              only with the parties you direct or as necessary to provide the
              Service
            </strong>.
            For example, if you choose colleges X, Y, Z to receive your video, we
            will make it available to those colleges (typically by allowing their
            admissions officers access). If your high school set up your account,
            your teachers or administrators may have access to your videos for
            evaluation. We will not send your identifiable videos to any third
            parties
            <strong>beyond those you or your school designate</strong>
            without separate permission. (We might share anonymized or aggregated
            data with researchers or use it for analytics.)
          </p>
        </li>
        <li>
          <p>
            <strong>Advertising and Marketing:</strong>
            Currently, VIVA does not display third-party advertising, nor do
            we sell personal data to marketers. If this changes in the future, we
            will update our policies and obtain any necessary consents. We reserve
            the right to use your name, likeness, and information internally for
            promotional purposes (for example, we might showcase a successful use
            case of VIVA to other schools or students, after anonymizing the
            content).
          </p>
        </li>
        <li>
          <p>
            <strong>Rights of Users:</strong>
            Depending on your jurisdiction, you may have legal rights regarding
            your personal data (such as rights to access or delete personal
            information). Our Privacy Policy provides details on how to exercise
            those rights and how to contact us with privacy questions. For example,
            if you are in the EU or UK, GDPR may provide certain data rights; if
            you are in California, the CCPA/CPRA may apply, etc. While the videos
            and content are our property from an intellectual property standpoint,
            we will still respect valid legal requests regarding personal data
            within those videos (such as your name or image), to the extent
            required by law.
          </p>
        </li>
      </ul>
      <p>
        By agreeing to these Terms, you also agree that you have reviewed our
        Privacy Policy. If you do not agree with our data practices, please do
        not use the Service.
      </p>

      <h3><strong>10. Sharing and Use of Videos by Third Parties</strong></h3>
      <p>
        When you use VIVA, one of the main purposes is to share your
        recorded interview video with third-party evaluators (like teachers or
        admissions officers). It’s important you understand how that works and
        what rights those third parties have or don’t have:
      </p>
      <ul>
        <li>
          <p>
            <strong>Your Consent to Share:</strong>
            By choosing to send or share your video with a teacher, school, or
            college, you are instructing and authorizing us to make your content
            available to that third party. We will typically do this through a
            portal or by giving them access to our platform where they can view
            (and possibly rate or comment on) your video. We consider this sharing
            as being
            <strong>at your direction</strong>,
            since you select the recipients.
          </p>
        </li>
        <li>
          <p>
            <strong>Third-Party Access:</strong>
            Once an authorized third party (e.g., an admissions officer at a
            college you applied to, or your high school teacher) has access to your
            video,
            <strong>we do not control what they do with it</strong>
            after viewing. We require such parties (through separate agreements or
            terms when they use our platform) to respect the confidentiality of
            the content and use it only for evaluation. However, we cannot
            guarantee their actions. For example, a teacher might download or
            screenshot parts of the video, or an admissions officer might share it
            with a colleague. By using VIVA, you acknowledge that these third
            parties are outside of our control, and we are not liable for their
            use or misuse of your content. If you are a teacher or administrator
            using VIVA to evaluate a student or applicant, you agree that you
            will keep their information confidential.
          </p>
        </li>
        <li>
          <p>
            <strong>No Guarantees by InitialView:</strong>
            We make no guarantees that sending a video will improve your chances
            of admission or grades (though we hope it will showcase your
            abilities). Similarly, we are not responsible for any decisions made
            by those third parties, such as a grade given by a teacher or an
            admissions outcome from a college.
          </p>
        </li>
        <li>
          <p>
            <strong>Feedback to Students:</strong>
            In some cases, your school or our platform might provide you with
            feedback or a rating after review. Any such feedback from third
            parties is solely the opinion of those reviewers and not of
            InitialView. If you have disputes or questions about a review or
            evaluation from a teacher/admissions officer, you should address it
            with them directly. We do not intervene in academic or admissions
            judgments.
          </p>
        </li>
        <li>
          <p>
            <strong>Confidentiality:</strong>
            While we will treat your videos as private to your account and your
            chosen reviewers, we cannot promise absolute confidentiality once
            shared. Colleges and schools are expected to handle applicant/student
            data carefully, but by the nature of the admissions and educational
            process, your video may become part of an application file or
            educational record. Those institutions may retain copies as per their
            own policies. Again, after we provide the content to them at your
            request, use may be subject to their policies.
          </p>
        </li>
      </ul>
      <p>
        In summary,
        <strong>
          we will facilitate the sharing of your content exactly as you direct
        </strong>,
        but we aren’t responsible for the actions or decisions of the people who
        view it. Use your best judgment when deciding where to send your video.
      </p>

      <h3><strong>11. Intellectual Property Rights (Our Content)</strong></h3>
      <p>
        Apart from User Content, all other content and materials on the VIVA
        platform are owned by or licensed to Aim Solutions, Inc.
        (InitialView) and are protected by intellectual property laws. This
        includes, for example:
      </p>
      <ul>
        <li>
          <p>The software and code that operates the platform.</p>
        </li>
        <li>
          <p>
            The design, layout, and user interface of the website/app.
          </p>
        </li>
        <li>
          <p>
            All text, graphics, logos, trademarks, service marks, and brand
            elements (e.g., the “VIVA” name and logo, “InitialView” name and
            logo).
          </p>
        </li>
        <li>
          <p>
            Any content created by us or our AI that is not specific to a user’s
            input (for instance, general guidance text, help articles, the
            structure of questions templates, etc., if applicable).
          </p>
        </li>
      </ul>
      <p>
        You
        <strong>must not use our trademarks or copyrighted material</strong>
        without our prior written consent, except as allowed under these Terms.
        You also may not remove or obscure any copyright, trademark, or other
        proprietary rights notices on the platform.
      </p>
      <p>
        We grant you a limited right to use and display materials from our
        Service for personal, educational use (as described in Section 7), but
        <strong>
          you may not copy, distribute, or create derivative works from our
          materials
        </strong>
        for any other purpose without explicit permission.
      </p>
      <p>
        If you provide any feedback or suggestions to us about how to improve
        VIVA or InitialView’s services, we may use that feedback without
        obligation to you. (In legal terms, you grant us a perpetual,
        royalty-free license to use any feedback or ideas you volunteer to us
        for any purpose).
      </p>

      <h3><strong>12. Indemnification</strong></h3>
      <p>
        <strong>
          You agree to indemnify, defend, and hold harmless Aim Solutions, Inc.
          (InitialView), and its affiliates, officers, directors, employees,
          agents, and partners from and against any and all claims, liabilities,
          damages, losses, and expenses (including reasonable attorneys’ fees)
        </strong>
        that arise out of or relate to:
      </p>
      <ul>
        <li>
          <p>
            Your use of the Service (including any content you submit, actions you
            take, or evaluations you make).
          </p>
        </li>
        <li>
          <p>
            Your violation of these Terms or any applicable law or regulation.
          </p>
        </li>
        <li>
          <p>
            Your infringement or violation of any rights of another person or
            entity (for example, if you upload a project you didn’t create and the
            actual author makes a claim, or if someone in your video didn’t
            consent to being recorded).
          </p>
        </li>
        <li>
          <p>
            Any misrepresentation made by you (for instance, if you falsely claim
            to have consent or to be of a certain age).
          </p>
        </li>
      </ul>
      <p>
        We reserve the right, at our own expense, to assume the exclusive
        defense and control of any matter otherwise subject to indemnification
        by you. In that case, you agree to cooperate with our defense of such
        claim. You will not settle any claim requiring indemnification without
        our prior written consent.
      </p>
      <p>
        This indemnity obligation continues even after you stop using VIVA
        or after termination of these Terms.
      </p>

      <h3><strong>13. Disclaimers of Warranties</strong></h3>
      <p>
        VIVA and all services provided by InitialView are offered on an
        “<strong>AS IS</strong>”
        and
        “<strong>AS AVAILABLE</strong>”
        basis. To the fullest extent permitted by law, we disclaim all warranties
        of any kind, whether express, implied, or statutory, regarding our
        Service. This includes, but is not limited to:
      </p>
      <ul>
        <li>
          <p>
            <strong>Implied Warranties:</strong>
            We do not make any implicit promises, such as warranties of
            merchantability, fitness for a particular purpose, title, or
            non-infringement. All such warranties are expressly disclaimed.
          </p>
        </li>
        <li>
          <p>
            <strong>Service Reliability:</strong>
            We do not guarantee that the platform will be uninterrupted,
            error-free, or secure. While we strive for high availability and have
            backup systems,
            <strong>
              we make no warranty that your uploads or recordings will always be
              saved, or that the Service will be available at any given moment
            </strong>.
            Downtime or data loss could occur for various reasons.
          </p>
        </li>
        <li>
          <p>
            <strong>Accuracy:</strong>
            We do not warrant the accuracy or completeness of any information or
            content obtained through the Service. For example, we do not warrant
            that the AI-generated questions will be perfectly tailored or that
            using the Service will result in any particular outcome (admission to
            a college, a certain grade, etc.).
          </p>
        </li>
        <li>
          <p>
            <strong>Third-Party Content:</strong>
            We make no warranty regarding any content provided by third parties
            (such as the questions from the AI, or content in external links, or
            evaluations given by teachers) or any third-party websites/services
            that integrate with VIVA.
          </p>
        </li>
        <li>
          <p>
            <strong>Safety and Security:</strong>
            While we implement security measures, we
            <strong>
              do not warrant that the Service is free of viruses or other harmful
              components
            </strong>.
            Any material downloaded or obtained through the Service is accessed at
            your own risk. You are solely responsible for any damage to your
            device or data that results from use of the Service.
          </p>
        </li>
      </ul>
      <p>
        <strong>No Guarantee of Results:</strong>
        You accept that using VIVA is not guaranteed to have any particular
        result for your academic or admissions outcomes. Colleges and teachers
        will make independent decisions – our platform is just a medium. You
        should use it as a tool for self-improvement and demonstration, but
        <strong>at your own risk as to the results</strong>.
      </p>
      <p>
        Some jurisdictions do not allow the exclusion of certain warranties, so
        some of the above exclusions may not apply to you. In such cases, any
        implied warranties are limited to the minimum duration permitted by
        applicable law.
      </p>

      <h3><strong>14. Limitation of Liability</strong></h3>
      <p>
        To the maximum extent permitted by law, in no event will Aim Solutions,
        Inc. (InitialView) or its affiliates, officers, employees, or agents be
        liable to you for any
        <strong>
          indirect, incidental, special, consequential, or punitive damages
        </strong>
        whatsoever. This includes, without limitation, damages for lost profits,
        lost opportunities, loss of data, emotional distress, or any other
        intangible losses arising out of or related to:
      </p>
      <ul>
        <li>
          <p>
            Your use of or inability to use VIVA (for example, an inability
            to record your video due to a technical failure, or a video being lost
            or inaccessible).
          </p>
        </li>
        <li>
          <p>
            Unauthorized access to or alteration of your transmissions or data
            (for instance, if someone unauthorized views or uses your video,
            though we try to prevent that).
          </p>
        </li>
        <li>
          <p>
            Statements or conduct of any third party on the Service (such as
            comments from other users, or evaluations by a teacher).
          </p>
        </li>
        <li>
          <p>Any other matter relating to the Service.</p>
        </li>
      </ul>
      <p>
        In no event will our total liability to you for all claims arising out
        of or relating to these Terms or the Service
        <strong>
          exceed the amount you paid to us (if any) for the use of the Service in
          the past twelve (12) months
        </strong>
        (or $100 USD, if greater). If your use was arranged by a school and you
        paid us nothing directly, our liability will be capped at a nominal amount
        (since our compensation in that case came from the school).
      </p>
      <p>
        <strong>Exceptions:</strong>
        Some jurisdictions do not allow the exclusion or limitation of certain
        damages, so the above limitations may not apply to you fully. In those
        jurisdictions, our liability will be limited to the greatest extent
        permitted by law.
      </p>
      <p>
        You agree that the limitations of liability and disclaimers in these
        Terms are fair and reasonable and that they form an essential basis of
        the bargain between you and us (i.e., we could not provide this service
        to you without these limitations).
      </p>

      <h3><strong>15. Termination of Service</strong></h3>

      <h4><strong>15.1 By You:</strong></h4>
      <p>
        You may stop using VIVA at any time. If you have an account, you
        can simply discontinue use. Keep in mind that
        <strong>no refunds</strong>
        will be given for any unused portion of the service unless required by law
        or explicitly provided in these Terms.
      </p>

      <h4><strong>15.2 By Us:</strong></h4>
      <p>
        We reserve the right to suspend or terminate your access to VIVA
        (in whole or in part) under the following circumstances:
      </p>
      <ul>
        <li>
          <p>
            <strong>Breach of Terms:</strong>
            If you violate any provision of these Terms or act in a manner that
            clearly shows you do not intend to or are unable to comply with these
            Terms, we may disable your account or access. For example, cheating,
            harassing others, or attempting to hack the site are grounds for]
            immediate termination.
          </p>
        </li>
        <li>
          <p>
            <strong>Legal Requirement:</strong>
            If we are required to do so by law (for example, if providing the
            Service to you becomes illegal in your region).
          </p>
        </li>
        <li>
          <p>
            <strong>Discontinuation or Change:</strong>
            We may terminate or suspend the Service if we discontinue the platform
            (e.g., if we decide to shut down VIVA in the future) or if
            technical or security issues arise.
          </p>
        </li>
        <li>
          <p>
            <strong>Non-Payment:</strong>
            If applicable, failure to pay any fees due may result in suspension of
            service until payment is resolved.
          </p>
        </li>
      </ul>
      <p>
        If we terminate your use for cause (due to your breach), we are not
        obligated to refund any fees you have paid. If we discontinue the
        Service entirely, we will provide pro-rata refunds for any prepaid
        services that were not delivered, where required.
      </p>

      <h4><strong>15.3 Effects of Termination:</strong></h4>
      <p>
        Upon termination, your right to access the Service will immediately cease.
        We may disable or delete your account at our discretion.
        <strong>
          However, we may retain content and data as described in these Terms
        </strong>.
        For example, we might keep your videos on file, especially if they’ve been
        shared with third parties, or if we need them for legal reasons or for our
        research/training purposes. Sections of these Terms that by their nature
        should survive termination (such as content licenses, disclaimers,
        limitations of liability, indemnification, and governing law)
        <strong>will continue to apply</strong>
        even after your access is terminated.
      </p>

      <h3><strong>16. Governing Law and Dispute Resolution</strong></h3>
      <p>
        These Terms and any dispute arising out of or related to the Service
        will be governed by and construed in accordance with the laws of the
        United States and the State of Georgia (without regard to its conflict
        of law principles).
      </p>
      <ul>
        <li>
          <p>
            <strong>Jurisdiction and Venue:</strong>
            Unless otherwise required by law (such as an exclusive legal right in
            your country), you agree that any disputes or claims will be resolved
            by the state or federal courts located in Atlanta, GA, and you consent
            to the personal jurisdiction of those courts.
          </p>
        </li>
        <li>
          <p>
            <strong>International Use:</strong>
            We recognize that VIVA is accessible globally. If you are
            accessing the Service from outside the United States, you do so on
            your own initiative and are responsible for compliance with local
            laws. We make no representations that the Service is appropriate or
            available for use in all locations. Local laws may differ and you must
            ensure you do not violate any export or import laws by using VIVA.
          </p>
        </li>
        <li>
          <p>
            <strong>Class Action Waiver:</strong>
            To the extent permissible by law, you and InitialView agree that
            <strong>
              each may bring claims against the other only in your or our
              individual capacity
            </strong>,
            and not as a plaintiff or class member in any purported class or
            representative proceeding.
          </p>
        </li>
      </ul>
      <p>
        Nothing in this section limits either party’s ability to seek injunctive
        or equitable relief in a court of law for matters related to
        intellectual property or unauthorized access to the Service.
      </p>

      <h3><strong>17. Miscellaneous</strong></h3>
      <ul>
        <li>
          <p>
            <strong>Entire Agreement:</strong>
            These Terms, along with the Privacy Policy and any additional
            guidelines or rules posted in specific areas of the Service,
            constitute the entire agreement between you and us regarding the use
            of VIVA. They supersede any prior agreements or communications
            (whether written or oral) about the Service.
          </p>
        </li>
        <li>
          <p>
            <strong>No Waiver:</strong>
            Our failure to enforce any provision of these Terms shall not be
            considered a waiver of that provision or of the right to enforce it in
            the future.
            <strong>Any waiver of any term must be in writing</strong>
            and signed by an authorized representative of InitialView to be
            effective.
          </p>
        </li>
        <li>
          <p>
            <strong>Severability:</strong>
            If any provision of these Terms is found by a court of competent
            jurisdiction to be invalid or unenforceable, that provision will be
            limited or eliminated to the minimum extent necessary, and the rest of
            the Terms will remain in full effect. In other words, the invalid part
            will be severed, but the rest remains binding.
          </p>
        </li>
        <li>
          <p>
            <strong>Assignment:</strong>
            You may not assign or transfer any of your rights or obligations under
            these Terms without our prior written consent. We may assign our
            rights and obligations to an affiliate or in connection with a merger,
            acquisition, or sale of assets, or by operation of law or otherwise.
            These Terms will bind and inure to the benefit of the parties, their
            successors, and permitted assigns.
          </p>
        </li>
        <li>
          <p>
            <strong>Relationship:</strong>
            Nothing in these Terms creates any agency, partnership, or joint
            venture between you and us. You and InitialView are independent
            contractors. You cannot represent on behalf of InitialView or bind us
            to any agreement.
          </p>
        </li>
        <li>
          <p>
            <strong>Headings:</strong>
            Section titles and headings in these Terms are for convenience only
            and have no legal or contractual effect.
          </p>
        </li>
        <li>
          <p>
            <strong>Force Majeure:</strong>
            We will not be liable for any failure or delay in our performance of
            obligations under these Terms if such failure or delay is due to
            causes beyond our reasonable control, such as acts of God, war,
            terrorism, pandemics, natural disasters, power or internet outages,
            labor disputes, or governmental action.
          </p>
        </li>
      </ul>

      <h3><strong>18. Contact Information</strong></h3>
      <p>
        If you have any questions, concerns, or feedback about these Terms or
        the Service, please contact us:
        <a href="mailto:contact@initialview.com">contact@initialview.com</a>.
      </p>
      <p>
        We will endeavor to respond to inquiries in a timely manner.
      </p>

      <hr />

      <p>
        By using the VIVA platform, you acknowledge that you have read
        these Terms and agree to abide by them. Thank you for reading, and we
        look forward to providing you with a valuable service to demonstrate
        your knowledge and integrity in your academic journey. Good luck with
        your VIVA!
      </p>
    </div>
  </div>
</template>

<script>
import Header from '@/components/Header.vue'

export default {
  name: 'TermsAndConditions',
  components: {
    Header
  }
}
</script>

<style scoped>
p {
  color: #333;
}

.title {
  background-color: var(--viva-green);
}

.title h1 {
  margin-top: 3rem;
  margin-bottom: 3rem;
  color: var(--viva-cream);
}

.content::before {
  margin-top: 3rem;
}

.content::after {
  margin-bottom: 6rem;
}
</style>